import type { Feature, LineString, Position } from 'geojson';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import useDTO from 'components/Map/hooks/useDTO';

import useMapbox from 'hooks/useMapBox';
import useUserGeoLocation from 'hooks/useUserGeoLocation';

import { DEFAULT_PADDING, DEFAULT_ZOOM, MAP_CENTER_TYPE } from './constants';
import type { BoundaryPartData, FlightResponse, LatLngLike, Props } from './types';

const offsetToPadding = (offset?: [number, number]) => {
    if (!offset) {
        return DEFAULT_PADDING;
    }

    const [offsetX, offsetY] = offset;

    return {
        bottom: DEFAULT_PADDING - offsetY,
        left: DEFAULT_PADDING + offsetX,
        right: DEFAULT_PADDING - offsetX,
        top: DEFAULT_PADDING + offsetY
    };
};

const createFlightPathFeatures = (flightPaths: FlightResponse[]): Feature<LineString>[] =>
    flightPaths.map(({ flightPath }) => ({
        geometry: {
            coordinates: flightPath.coordinates as Position[],
            type: 'LineString' as const
        },
        id: uuidv4(),
        properties: {},
        type: 'Feature' as const
    }));

const useMapRecenter = ({ flightPaths, focusGeometry, geometry, map, mapOptions }: Props) => {
    const { center: initialCenter, centerOffset, zoom } = mapOptions;

    const { fitBoundsToFeatures } = useMapbox();
    const { convertFromDTOs } = useDTO();
    const { getUserGeolocation, userGeoLocation } = useUserGeoLocation();

    const [centerCycle, setCenterCycle] = useState<MAP_CENTER_TYPE[]>();
    const centerTypeIndexRef = useRef(0);
    const hasCenteredOnPartsRef = useRef(false);
    const hasInitialCenterRef = useRef(false);
    const previousGeometryRef = useRef(geometry?.parts);
    const previousFlightPathsRef = useRef(flightPaths);

    useEffect(() => {
        if (!map) {
            return;
        }

        const geometryChanged = previousGeometryRef.current !== geometry?.parts;
        const flightPathsChanged = previousFlightPathsRef.current !== flightPaths;

        if (geometryChanged || flightPathsChanged) {
            hasInitialCenterRef.current = false;
            previousGeometryRef.current = geometry?.parts;
            previousFlightPathsRef.current = flightPaths;
        }

        if (hasInitialCenterRef.current) {
            return;
        }

        const focusParts = focusGeometry?.parts;

        if (focusParts && focusParts.length > 0) {
            centerOnParts(focusParts);
            hasInitialCenterRef.current = true;

            return;
        }

        const hasFlights = flightPaths && flightPaths.length > 0;
        const allParts = geometry?.parts;
        const hasBoundary = allParts && allParts.length > 0;

        if (hasFlights || hasBoundary) {
            centerOnAllVisibleFeatures({
                flightPaths: hasFlights ? flightPaths : undefined,
                parts: hasBoundary ? allParts : undefined
            });
            hasInitialCenterRef.current = true;

            return;
        }

        hasCenteredOnPartsRef.current = false;
        void centerOnUserLocation();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, focusGeometry?.parts, geometry?.parts, flightPaths]);

    useEffect(() => {
        if (!map || !userGeoLocation || hasCenteredOnPartsRef.current) {
            return;
        }

        map.jumpTo({
            center: userGeoLocation as LatLngLike,
            zoom: DEFAULT_ZOOM
        });
    }, [map, userGeoLocation]);

    const centerOnParts = (parts: BoundaryPartData[]) => {
        const features = convertFromDTOs(parts);

        hasCenteredOnPartsRef.current = true;
        fitBoundsToFeatures(map, features, offsetToPadding(centerOffset));
    };

    const centerOnUserLocation = async () => {
        if (!map) {
            return;
        }

        const userLocation = await getUserGeolocation();

        if (!userLocation) {
            return;
        }

        map.jumpTo({
            center: userLocation as LatLngLike,
            zoom: DEFAULT_ZOOM
        });
    };

    const centerOnInitialCenter = async () => {
        if (!initialCenter) {
            return;
        }

        map?.easeTo({
            center: initialCenter as LatLngLike,
            duration: 100,
            offset: centerOffset,
            zoom: zoom || DEFAULT_ZOOM
        });
    };

    const centerOnAllVisibleFeatures = ({
        flightPaths: flights,
        parts
    }: {
        flightPaths?: FlightResponse[];
        parts?: BoundaryPartData[];
    }) => {
        const flightFeatures = flights ? createFlightPathFeatures(flights) : [];
        const boundaryFeatures = parts ? convertFromDTOs(parts) : [];
        const allFeatures = [...flightFeatures, ...boundaryFeatures];

        if (allFeatures.length === 0) {
            return;
        }

        hasCenteredOnPartsRef.current = true;
        fitBoundsToFeatures(map, allFeatures, offsetToPadding(centerOffset));
    };

    const centerOnBoundaries = async () => {
        const { parts } = geometry || { parts: [] };

        centerOnAllVisibleFeatures({
            flightPaths: flightPaths && flightPaths.length > 0 ? flightPaths : undefined,
            parts: parts && parts.length > 0 ? parts : undefined
        });
    };

    const centerOnVisibleArtifacts = ({
        isBoundaryVisible: boundaryVisible,
        isFlightPathsVisible: flightPathsVisible
    }: {
        isBoundaryVisible: boolean;
        isFlightPathsVisible: boolean;
    }) => {
        const hasVisibleFlights = flightPathsVisible && flightPaths && flightPaths.length > 0;
        const parts = geometry?.parts;
        const hasVisibleBoundary = boundaryVisible && parts && parts.length > 0;

        centerOnAllVisibleFeatures({
            flightPaths: hasVisibleFlights ? flightPaths : undefined,
            parts: hasVisibleBoundary ? parts : undefined
        });
    };

    const getCenterCycle = async () => {
        if (centerCycle !== undefined) {
            return centerCycle;
        }

        const availableCenterTypes = [];

        if (focusGeometry || geometry) {
            availableCenterTypes.push(MAP_CENTER_TYPE.BOUNDARY_CENTROID);
        }

        const userLocation = await getUserGeolocation();

        if (userLocation) {
            availableCenterTypes.push(MAP_CENTER_TYPE.USER_LOCATION);
        }

        if (availableCenterTypes.length === 0) {
            availableCenterTypes.push(MAP_CENTER_TYPE.INITIAL_CENTER);
        }

        setCenterCycle(availableCenterTypes);

        return availableCenterTypes;
    };

    const CENTER_FUNCTION_MAP: Record<MAP_CENTER_TYPE, () => Promise<void>> = {
        [MAP_CENTER_TYPE.INITIAL_CENTER]: centerOnInitialCenter,
        [MAP_CENTER_TYPE.BOUNDARY_CENTROID]: centerOnBoundaries,
        [MAP_CENTER_TYPE.USER_LOCATION]: centerOnUserLocation
    };

    const handleChangeMapCenter = async () => {
        const nextCenterType = (await getNextCenterType()) as MAP_CENTER_TYPE;

        if (!nextCenterType) {
            return;
        }

        const centerFunction = CENTER_FUNCTION_MAP[nextCenterType];

        if (!centerFunction) {
            return;
        }

        await centerFunction();
    };

    const getNextCenterType = async () => {
        const cycle = await getCenterCycle();

        if (!cycle) {
            return;
        }

        const nextIndex = (centerTypeIndexRef.current + 1) % cycle.length;

        const nextCenterType = cycle[nextIndex];

        centerTypeIndexRef.current = nextIndex;

        return nextCenterType;
    };

    return {
        centerOnVisibleArtifacts,
        handleChangeMapCenter
    };
};

export default useMapRecenter;
